import { Component, OnInit, OnDestroy, ChangeDetectorRef, ElementRef, Renderer2 } from '@angular/core';
import { NgForm, FormBuilder } from '@angular/forms';
import { MatTableDataSource } from '@angular/material/table';
import { ViewTableComponent } from '../../base/view-table/view-table.component';
import { PackageEntityComponent } from '../../forms/package-entity/package-entity.component';
import { PageEvent } from '@angular/material/paginator';
import { HttpParameter } from 'src/app/types/http-parameter';
import { ComponentRefreshService } from 'src/app/services/component-refresh.service';
import { Subscription } from 'rxjs';
import { Router, ActivatedRoute } from '@angular/router';
import { DialogService } from 'src/app/services/dialog.service';
import { EntityRESTService } from 'src/app/services/entity-rest.service';
import { UtilityService } from 'src/app/services/utility.service';
import { FieldsService } from 'src/app/services/fields.service';
import { DrupalRESTService } from 'src/app/services/drupal-rest.service';
import { TaxonomyService } from 'src/app/services/taxonomy.service';
import { AuthRESTService } from 'src/app/services/auth-rest.service';
import { AuthService } from 'src/app/services/auth.service';
import { MenuRESTService } from 'src/app/services/menu-rest.service';
import { RouteEventsService } from 'src/app/route-events.service';
import { PermissionsService } from 'src/app/services/permissions.service';
import { ConfigPagesService } from 'src/app/services/config-pages.service';
import { PackagesService } from 'src/app/services/packages.service';
import { MatSnackBar } from '@angular/material/snack-bar';
import { UserService } from 'src/app/services/user.service';
import { DomSanitizer } from '@angular/platform-browser';
import { RefreshService } from 'src/app/services/refresh.service';
import { LoggerService } from '../../../services/logger.service';

@Component({
  selector: 'app-package-list',
  templateUrl: './package-list.component.html',
  styleUrls: ['./package-list.component.css']
})
export class PackageListComponent extends ViewTableComponent implements OnInit, OnDestroy {

  // Subscription to the refresh service
  private refreshSubscription: Subscription;

  // Other component refs.
  PackageEntityComponent = PackageEntityComponent

  override displayedColumns: string[] = [
    'id',
    'field_abbreviation',
    'field_lesson_count_value_1',
    'field_lesson_price_value',
    'field_lesson_type',
    'field_package_name',
    'field_sps_code',
    'field_total_price_value',
    'field_custom_text',
  ];

  // Which tab this route serves. Set from route data (`retired: true`) so
  // /packages-list and /packages-list-retired share this one component.
  isRetired = false;
  links = [
    { linkTitle: 'Active Packages', linkRoute: '/packages-list', retired: false },
    { linkTitle: 'Retired Packages', linkRoute: '/packages-list-retired', retired: true }
  ];

  override ngOnInit() {
    // Must be resolved before super.ngOnInit() fires the first getData().
    this.isRetired = this._activatedRoute.snapshot.data?.['retired'] === true;

    // Initialize pagination values
    this.paginationIndex = 0;
    this.pageSize = 25;  // Set default page size

    // Setup refresh service before parent initialization
    this.refreshSubscription = this._componentRefreshService.packageListRefresh$.subscribe(() => {
      LoggerService.log('PackageListComponent: Received refresh event');
      this.refreshData();
    });

    // Call parent ngOnInit
    super.ngOnInit();
  }

  override ngOnDestroy() {
    // Unsubscribe to prevent memory leaks
    if (this.refreshSubscription) {
      this.refreshSubscription.unsubscribe();
    }

    // Call parent ngOnDestroy if it exists
    super.ngOnDestroy();
  }

  /**
   * Refreshes the data
   */
  refreshData(): void {
    LoggerService.log('PackageListComponent: Refreshing data');
    this.getData(this.queryParams);
  }

  openPackageEntityComponent(action: string, EntityID?) {
    this._dialogService.openDialog(PackageEntityComponent, "defaultWithData", {
      data: {
        EntityID: EntityID,
        action: action,
      },
    }).afterClosed().subscribe(data => {
      this.getData(this.queryParams);
    });
  }

  override getData(params: { parameter: string; value: string; }[]) {
    // Show Progress Bar (Loading indicator)
    this.ShowProgressBar = true;

    // Store only non-pagination params in queryParams
    this.queryParams = params.filter(param =>
      param.parameter !== 'page' && param.parameter !== 'items_per_page'
    );

    // Add current pagination state to params if not present
    let finalParams = [...params];
    if (!params.some(p => p.parameter === 'page')) {
      finalParams.push(
        { parameter: 'page', value: this.paginationIndex.toString() },
        { parameter: 'items_per_page', value: this.pageSize.toString() }
      );
    }

    // Add sorting parameters if available
    if (this.sortParams !== undefined) {
      finalParams.push(this.sortParams);
    }

    // Scope to the active tab (active vs retired packages).
    finalParams = [
      ...finalParams,
      { parameter: "field_retired_op", value: "=" },
      { parameter: "field_retired_value", value: this.isRetired ? "1" : "0" }
    ]

    // Load the new data with the filtering parameters.
    this._utilityService.getPackagesListView(
      finalParams
    )
      .subscribe((data) => {
        // ShowProgressBar
        this.ShowProgressBar = false;

        this.data = data;
        this.dataSource = new MatTableDataSource(data['results']);

        // Pagination
        if (data['results']) {
          this.setupPagination(data, params);
        }
      },
        error => this.handleError(error)
      )
  }

  override resetForm() {
    // Clear all stored params
    this.queryParams = [];
    this.sortParams = undefined;

    // Reset pagination
    if (this.paginator) {
      this.paginator.pageIndex = 0;
      this.paginationIndex = 0;  // Reset the pagination index
      this.pageSize = 25;  // Reset page size
    }

    // Clear the form
    this.form.resetForm();

    // Make a fresh request with explicitly empty params
    this.getData([
      { parameter: 'page', value: '0' },
      { parameter: 'items_per_page', value: '10' }
    ]);

    // Update router last
    this.updateRouterParams({});
  }

  override pageChanged(event: PageEvent) {
    LoggerService.log('Custom pageChanged called...');

    // Update pagination state
    this.paginationIndex = event.pageIndex;
    this.pageSize = event.pageSize;

    // Build pagination params for router
    let paginationParams = {
      page: event.pageIndex.toString(),
      items_per_page: event.pageSize.toString(),
      timestamp: new Date().getTime().toString()
    };

    this.updateRouterParams({ ...this.routeParams, ...paginationParams });
  }

  override setupPagination(data: any, params: { parameter: string; value: string; }[]) {
    const pageParam = params.find(param => param.parameter === 'page');

    // Check if paginator exists before accessing its properties
    if (this.paginator) {
      this.paginator.length = data?.['count'] ?? 0;
      this.paginator.pageSize = this.pageSize;
      this.paginator.pageIndex = pageParam ? parseInt(pageParam.value, 10) : 0;
    }

    // DO NOT call updateRouterParams here - this prevents duplicate requests
    // The routing has already been handled by pageChanged() or other methods
    LoggerService.log('setupPagination called - skipping route update to prevent duplicates');
  }

  override onPageSizeOptions() {
    return [10, 25, 50];
  }
}
